import React, { useState, useMemo, useCallback } from 'react';
import { Card, Badge, Button, Row, Col, Modal, Form, Spinner } from 'react-bootstrap';
import moment from 'moment';
import showNotification from 'components/extras/showNotification';
import { updateIntervention } from 'pages/planning/hooks/planningService';
import CreateEditDevisModal from '../DevisModal';
import NewDevisModal from 'pages/Devis/NewDevisModal';

const DevisTab = ({ intervention, setIntervention }) => {
    // --- ÉTATS ---
    const [showModal, setShowModal] = useState(false);
    const [editingDevis, setEditingDevis] = useState(null);

    // --- DATA ---
    const devisList = useMemo(() =>
        intervention?.rapport_intervention?.devis || []
        , [intervention]);

    // Helpers pour l'identification
    const getDevisId = (devis) => devis?._id || devis?.id;
    const formatMontant = (m) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(m);

    // --- LOGIQUE DE MISE À JOUR (SUCCÈS MODAL) ---
    const handleDevisSuccess = async (newOrUpdatedDevis) => {
        const devisId = getDevisId(newOrUpdatedDevis);

        let updatedList;
        const exists = devisList.find(d => getDevisId(d) === devisId);

        if (exists) {
            // Update
            updatedList = devisList.map(d => getDevisId(d) === devisId ? newOrUpdatedDevis : d);
        } else {
            // Create
            updatedList = [newOrUpdatedDevis, ...devisList];
        }

        try {
            // 1. Préparation du payload pour MongoDB
            const payload = {
                rapport_intervention: {
                    ...(intervention?.rapport_intervention || {}),
                    devis: updatedList
                },
            };

            // 2. Appel API pour persister les données
            await updateIntervention(intervention?._id, payload);

            // 3. Mise à jour de l'état local seulement après le succès de l'API
            setIntervention(prev => ({
                ...prev,
                rapport_intervention: payload.rapport_intervention
            }));

            showNotification('Succès', exists ? 'Devis mis à jour' : 'Devis ajouté à l\'intervention', 'success');

            // 4. Fermeture propre
            setShowModal(false);
            setEditingDevis(null);

        } catch (err) {
            console.error("❌ Erreur lors de la mise à jour de l'intervention:", err);
            showNotification('Erreur', 'Impossible de lier le devis à l\'intervention', 'danger');
        }
    };

    const handleRemoveDevis = async (id) => {
        if (!window.confirm("Voulez-vous retirer ce devis de l'intervention ?")) return;

        const updatedList = devisList.filter(d => getDevisId(d) !== id);

        try {
            // Appel API direct pour la suppression
            await updateIntervention(intervention?._id, {
                rapport_intervention: {
                    ...intervention.rapport_intervention,
                    devis: updatedList
                }
            });

            setIntervention(prev => ({
                ...prev,
                rapport_intervention: {
                    ...prev.rapport_intervention,
                    devis: updatedList
                }
            }));
        } catch (err) {
            console.error("Erreur lors de la suppression du devis", err);
        }
    };

    const handleOpenModal = (devis = null) => {
        setEditingDevis(devis);
        setShowModal(true);
    };
    const getStatutColor = useCallback((statut) => {
        const map = {
            brouillon: 'secondary',
            envoyé: 'info',
            accepté: 'success',
            refusé: 'danger',
            transformé_commande: 'primary',
            expiré: 'warning',
        };
        return map[statut] || 'secondary';
    }, []);

    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <div className="p-3">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <h5 className="fw-bold mb-0 text-dark fs-6 fs-md-5">
                    Devis associés ({devisList.length})
                </h5>
                <Button variant="primary" size="sm" className="rounded-pill px-3 shadow-sm" onClick={() => setIsModalOpen(true)}>
                    Nouveau Devis
                </Button>
            </div>

            {/* LISTE DES DEVIS EN CARDS */}
            <Row className="g-3">
                {devisList.map((devis, idx) => {
                    const dId = getDevisId(devis);
                    const devisStatut = devis.statut || 'en_attente';

                    // Définition dynamique de la couleur selon le statut

                    return (
                        <Col key={dId || idx} xs={12} md={6} lg={4}>
                            <Card className={`border-0  rounded-4 h-100`}>
                                <Card.Body className="p-3 d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <Badge bg={getStatutColor(devis?.statut)} pill className="text-capitalize">
                                            {devisStatut}
                                        </Badge>
                                        <Button variant="link" className="p-0 text-primary" onClick={() => window.open(`/devis/${dId}`, '_blank')}>Ouvrir page
                                        </Button>
                                    </div>

                                    {/* Référence en grand */}
                                    <h5 className="fw-bold text-dark mb-0">
                                        {devis.reference}
                                    </h5>

                                    {/* Nom/Titre en muted et plus petit */}
                                    <div className="text-muted small text-truncate mb-2" style={{ maxWidth: '100%' }}>
                                        {devis.name || "Aucun titre"}
                                    </div>

                                    <div className="text-primary fw-bold fs-5 mb-2">
                                        {formatMontant(devis.totalTTC || 0)}
                                    </div>

                                    {/* Créé par au lieu de Validité */}
                                    <div className="small text-muted mb-3 mt-1">
                                        Créé par : <span className="fw-BOLD text-dark">{devis.createdBy?.name || "N/A"}</span>
                                    </div>
                                    <div className="small text-muted mb-3 mt-1">
                                        Créé le : <span className="fw-medium text-dark">{moment(devis.createdAt).format("DD/MM/YYYY")}</span>
                                    </div>

                                    <div className="d-flex gap-2 pt-2 border-top mt-auto">
                                        <Button variant="light" size="sm" className="flex-grow-1 border-0 text-secondary" onClick={() => handleOpenModal(devis)}>
                                            Modifier
                                        </Button>
                                        {/* <Button variant="danger" size="sm" className="flex-grow-1  border-0" onClick={() => handleRemoveDevis(dId)}>
                                            Supprimer
                                        </Button> */}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* MODAL EXTERNE */}
            <CreateEditDevisModal
                isOpen={showModal}
                setIsOpen={setShowModal}
                onSuccess={handleDevisSuccess}
                onClose={() => {
                    setShowModal(false);
                    setEditingDevis(null);
                }}
                interventionClient={intervention?.client}
                intervention={intervention || {}}
                devisId={editingDevis ? getDevisId(editingDevis) : null}
                devisData={editingDevis}
            />

            {isModalOpen && <NewDevisModal
                show={isModalOpen}
                onHide={() => setIsModalOpen(false)}
                onSuccess={async (newDevis) => {
                    await handleDevisSuccess(newDevis)
                    setEditingDevis(newDevis)
                    setShowModal(true)
                }}
                fromIntervention={true}
                interventionData={intervention}

            />}
        </div>
    );
};

export default DevisTab;